import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { FiRefreshCw, FiStopCircle, FiTrash2 } from "react-icons/fi";
import { useTranslations } from "./useHook";
import {
  usePermission,
  SystemPermissions,
} from "../../Utils/permissionAccess";
import Loader from "../../Utils/loader";

const inputClass =
  "w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all";

function statusBadge(status) {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    processing: "bg-blue-100 text-blue-800",
    completed: "bg-emerald-100 text-emerald-800",
    failed: "bg-red-100 text-red-800",
    cancelled: "bg-gray-200 text-gray-700",
  };
  return map[status] || "bg-gray-100 text-gray-700";
}

export default function Translations() {
  const {
    getModules,
    getStats,
    getJobs,
    generate,
    retryJob,
    stopJob,
    deleteJob,
  } = useTranslations();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(SystemPermissions.Translations.CREATE);

  const [modules, setModules] = useState([]);
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [moduleKey, setModuleKey] = useState("");
  const [selectedFields, setSelectedFields] = useState([]);
  const [onlyUntranslated, setOnlyUntranslated] = useState(true);
  const [overwrite, setOverwrite] = useState(false);

  const selectedModule = useMemo(
    () => modules.find((m) => m.key === moduleKey) || null,
    [modules, moduleKey],
  );

  const groupedModules = useMemo(() => {
    const groups = {};
    modules.forEach((m) => {
      if (!groups[m.group]) groups[m.group] = [];
      groups[m.group].push(m);
    });
    return groups;
  }, [modules]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [mods, st, jobData] = await Promise.all([
        getModules(),
        getStats(),
        getJobs({ offset: 0, limit: 20 }),
      ]);
      setModules(mods || []);
      setStats(st || null);
      setJobs(jobData.items || []);
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to load translations", "error");
    } finally {
      setLoading(false);
    }
  }, [getModules, getStats, getJobs]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Poll while any job is active
  useEffect(() => {
    const active = jobs.some(
      (j) => j.status === "pending" || j.status === "processing",
    );
    if (!active) return undefined;
    const timer = setInterval(async () => {
      try {
        const [st, jobData] = await Promise.all([
          getStats(),
          getJobs({ offset: 0, limit: 20 }),
        ]);
        setStats(st || null);
        setJobs(jobData.items || []);
      } catch {
        /* ignore poll errors */
      }
    }, 2500);
    return () => clearInterval(timer);
  }, [jobs, getStats, getJobs]);

  useEffect(() => {
    if (!selectedModule) {
      setSelectedFields([]);
      return;
    }
    setSelectedFields(selectedModule.fields.map((f) => f.key));
  }, [selectedModule]);

  const toggleField = (key) => {
    setSelectedFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleGenerate = async () => {
    if (!moduleKey) {
      Swal.fire("Required", "Please select a module.", "warning");
      return;
    }
    if (!selectedFields.length) {
      Swal.fire("Required", "Please select at least one field.", "warning");
      return;
    }

    const confirm = await Swal.fire({
      title: "Generate Urdu?",
      html: `
        <p class="text-sm text-left">
          Module: <strong>${selectedModule?.label || moduleKey}</strong><br/>
          Fields: <strong>${selectedFields.length}</strong><br/>
          Only untranslated: <strong>${onlyUntranslated ? "Yes" : "No"}</strong><br/>
          Overwrite existing: <strong>${overwrite ? "Yes" : "No"}</strong>
        </p>
        <p class="text-xs text-gray-500 mt-2 text-left">
          This creates a background job. Review generated Urdu in each module afterwards.
        </p>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Generate Urdu",
    });
    if (!confirm.isConfirmed) return;

    setSubmitting(true);
    try {
      const job = await generate({
        module: moduleKey,
        fields: selectedFields,
        onlyUntranslated,
        overwrite,
      });
      Swal.fire(
        "Started",
        `Job #${job?.id || ""} started (${job?.total_records ?? 0} records).`,
        "success",
      );
      await refresh();
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to start job", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = async (id) => {
    try {
      await retryJob(id);
      Swal.fire("Retry started", `Retrying failed items for job #${id}`, "success");
      await refresh();
    } catch (error) {
      Swal.fire("Error", error.message || "Retry failed", "error");
    }
  };

  const handleStop = async (id) => {
    const confirm = await Swal.fire({
      title: "Stop this job?",
      text: "The job will halt after the current record.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Stop job",
    });
    if (!confirm.isConfirmed) return;
    try {
      await stopJob(id);
      Swal.fire("Stopped", `Stop requested for job #${id}`, "success");
      await refresh();
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to stop job", "error");
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete this job?",
      text: "If it is running it will be stopped, then removed from the list.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc2626",
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteJob(id);
      Swal.fire("Deleted", `Job #${id} deleted`, "success");
      await refresh();
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to delete job", "error");
    }
  };

  if (loading) return <Loader />;

  const byStatus = stats?.jobsByStatus || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Translation Management
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Bulk-generate Urdu drafts from English. Dictionary fields use fixed
            maps; free text uses the translation API. Always review before
            publishing.
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Provider: {stats?.provider || "mymemory"}
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="btn btn-reset-filter justify-center"
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ["Pending", byStatus.pending || 0],
          ["Processing", byStatus.processing || 0],
          ["Completed", byStatus.completed || 0],
          ["Failed", byStatus.failed || 0],
          ["Cancelled", byStatus.cancelled || 0],
        ].map(([label, value]) => (
          <div
            key={label}
            className="rounded-xl border border-gray-200 bg-white p-4"
          >
            <p className="text-xs uppercase tracking-wide text-gray-500">
              {label}
            </p>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{value}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <h3 className="text-sm font-semibold text-gray-800">Generate Urdu</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Module
            </label>
            <select
              className={inputClass}
              value={moduleKey}
              onChange={(e) => setModuleKey(e.target.value)}
            >
              <option value="">Select module</option>
              {Object.entries(groupedModules).map(([group, opts]) => (
                <optgroup key={group} label={group}>
                  {opts.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="space-y-3 pt-7">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={onlyUntranslated}
                onChange={(e) => setOnlyUntranslated(e.target.checked)}
              />
              Only untranslated records
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
              />
              Overwrite existing Urdu
            </label>
          </div>
        </div>

        {selectedModule && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Fields</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {selectedModule.fields.map((f) => (
                <label
                  key={f.key}
                  className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selectedFields.includes(f.key)}
                    onChange={() => toggleField(f.key)}
                  />
                  <span>
                    {f.label}
                    <span className="ml-1 text-[10px] uppercase text-gray-400">
                      {f.mode}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            disabled={submitting || !canCreate}
            onClick={handleGenerate}
            className={`btn btn-primary justify-center ${
              submitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {submitting ? "Starting..." : "Generate Urdu"}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-800">Recent jobs</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Module</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3">Success</th>
                <th className="px-4 py-3">Failed</th>
                <th className="px-4 py-3">Skipped</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    No translation jobs yet.
                  </td>
                </tr>
              ) : (
                jobs.map((job) => (
                  <tr key={job.id} className="border-t border-gray-100">
                    <td className="px-4 py-3 font-medium">#{job.id}</td>
                    <td className="px-4 py-3">{job.module_key}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadge(
                          job.status,
                        )}`}
                      >
                        {job.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {job.processed_records}/{job.total_records}
                    </td>
                    <td className="px-4 py-3 text-emerald-700">
                      {job.success_count}
                    </td>
                    <td className="px-4 py-3 text-red-600">
                      {job.failed_count}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {job.skipped_count}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        {(job.status === "pending" ||
                          job.status === "processing") && (
                          <button
                            type="button"
                            className="btn btn-edit text-white"
                            onClick={() => handleStop(job.id)}
                          >
                            <FiStopCircle className="text-white" /> Stop
                          </button>
                        )}
                        {job.failed_count > 0 &&
                          job.status !== "processing" &&
                          job.status !== "pending" && (
                            <button
                              type="button"
                              className="btn btn-action text-white"
                              onClick={() => handleRetry(job.id)}
                            >
                              <FiRefreshCw className="text-white" /> Retry Failed
                            </button>
                          )}
                        <button
                          type="button"
                          className="btn btn-delete text-white"
                          onClick={() => handleDelete(job.id)}
                        >
                          <FiTrash2 className="text-white" /> Delete
                        </button>
                      </div>
                      {job.error_message && (
                        <p
                          className="text-[11px] text-red-500 mt-1 max-w-[220px] truncate"
                          title={job.error_message}
                        >
                          {job.error_message}
                        </p>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
